import React, { Fragment, useRef, useState } from "react"
import {
  Box, Flex, List, Stack,
} from "@chakra-ui/react"
import EventResourceListItem from "components/layout/EventResourceListItem"
import {
  Route,
  Switch,
  useLocation,
  useParams,
} from "react-router-dom"
import {
  AccountUser,
  DioboxEvent,
  Form,
  StyleAndConfigurationSubpath,
} from "sharedTypes"
import SimpleBar from "simplebar-react"
import Icon from "components/elements/Icon"
import ScrollToTop from "components/Navigation/ScrollToTop"
import { eventFormsStyleAndConfigurationPath, eventPortalWalletPassBuilderPath } from "utilities/routes"
import WalletPassCustomization from "components/WalletPass/WalletPassCustomization"
import WalletPassUpgradeAlert from "components/WalletPass/WalletPassUpgradeAlert"
import TitleWithSubtitle from "pages/EventShow/Forms/TitleWithSubtitle"
import useFeatureFlag from "services/featureFlags"
import usePermissions from "services/permissions"
import MenuCategory from "./MenuCategory"
import PortalSetup from "./PortalSetup"
import PortalOverview from "./PortalOverview"
import GuestActions from "./GuestActions"
import Confirmations from "./Confirmations"
import EmailResponders from "./EmailResponders"
import PortalPreview from "./PortalPreview/PortalPreview"
import StyleAndConfigurationModals, { StyleAndConfigurationModalName } from "./StyleAndConfigurationModals/StyleAndConfigurationModals"

type Props = {
  event: DioboxEvent
  updateEvent: (params: {}) => Promise<void>
  onChangeEventWorkingCopy: (values: Partial<DioboxEvent>) => void
  eventWorkingCopy: DioboxEvent
  forms: Form[]
  accountUsers: AccountUser[]
  onResetEventWorkingCopy: () => void
  variant?: "forms" | "portal"
  pathFor?: (subPath?: StyleAndConfigurationSubpath) => string
}

type MenuItem = { name: string, path?: StyleAndConfigurationSubpath, icon: string }

const StyleAndConfiguration = ({
  event: { id: eventId },
  event,
  updateEvent,
  onChangeEventWorkingCopy,
  eventWorkingCopy,
  forms,
  accountUsers,
  onResetEventWorkingCopy,
  variant = "forms",
  pathFor = (subPath?: StyleAndConfigurationSubpath) => (
    eventFormsStyleAndConfigurationPath(eventId, subPath)
  ),
}: Props) => {
  const location = useLocation()
  const { subpath } = useParams<{ subpath?: string }>()
  const ref = useRef(null)
  const [selectedModal, setSelectedModal] = useState<StyleAndConfigurationModalName | null>(null)
  const { walletPass: walletPassFlag } = useFeatureFlag()
  const { canViewSettings } = usePermissions()
  const walletPassEnabled = Boolean(walletPassFlag) && canViewSettings

  const menuItems: { categoryName: string | null, items: MenuItem[] }[] = variant === "portal" ? [
    {
      categoryName: null,
      items: [
        { name: "Design", path: undefined, icon: "branding-outline" },
        { name: "Overview", path: StyleAndConfigurationSubpath.Overview, icon: "site-homepage" },
        { name: "Guest Actions", path: StyleAndConfigurationSubpath.GuestActions, icon: "guest-search-settings" },
        { name: "Guest Confirmations", path: StyleAndConfigurationSubpath.GuestConfirmations, icon: "check-in" },
        { name: "Automated Emails", path: StyleAndConfigurationSubpath.AutomatedEmails, icon: "email-responders-outline" },
      ],
    },
  ] : [
    {
      categoryName: "Style & Branding",
      items: [
        { name: "Preview", path: undefined, icon: "home" },
      ],
    },
    {
      categoryName: "Settings",
      items: [
        { name: "Portal Setup", path: StyleAndConfigurationSubpath.PortalSetup, icon: "guest-search-settings" },
        { name: "Confirmations", path: StyleAndConfigurationSubpath.Confirmations, icon: "check-in" },
        { name: "Email Responders", path: StyleAndConfigurationSubpath.EmailResponders, icon: "email-responders-outline" },
      ],
    },
  ]

  const handleCloseModal = () => setSelectedModal(null)

  return (
    <Flex w="full">
      <List spacing={0} w="250px" flexShrink={0} borderRightWidth={1} h="full" data-testid="style-and-configuration-sidemenu">
        {menuItems.map((category) => (
          <Fragment key={category.categoryName ?? "portal"}>
            {category.categoryName && <MenuCategory name={category.categoryName} />}
            {category.items.map((item) => (
              <EventResourceListItem
                key={item.name}
                path={pathFor(item.path)}
                selected={item.path === subpath}
                p={5}
              >
                <Flex alignItems="center" whiteSpace="nowrap">
                  <Icon icon={item.icon} mr={3} color="blue.400" size={5} flexShrink={0} />
                  {item.name}
                </Flex>
              </EventResourceListItem>
            ))}
          </Fragment>
        ))}
      </List>
      <Flex w="full">
        <Switch location={location}>
          <Route
            exact
            path={pathFor()}
          >
            <PortalPreview
              event={event}
              forms={forms}
              onSelectModal={setSelectedModal}
              updateEvent={updateEvent}
            />
          </Route>
          <Route>
            <Flex flexDir="column" flexGrow={1} w="50%" bg="gray.50">
              <Box
                as={SimpleBar}
                h={1}
                flexGrow={1}
                borderRightWidth={1}
                scrollableNodeProps={{ ref }}
              >
                <Box p={10}>
                  <Switch location={location}>
                    {variant === "portal" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.Overview)}>
                        <PortalOverview event={event} />
                      </Route>
                    )}
                    {variant === "portal" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.GuestActions)}>
                        <GuestActions event={event} updateEvent={updateEvent} />
                      </Route>
                    )}
                    {variant === "portal" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.GuestConfirmations)}>
                        <Confirmations updateEvent={updateEvent}>
                          {walletPassEnabled && (
                            <Stack spacing={4} mt={16}>
                              <TitleWithSubtitle
                                title="Wallet Pass Customization"
                                subtitle="Design your wallet pass, including logo, banner image, text, and back-of-pass details, in one editor for both Apple and Google Wallet."
                                mb={0}
                              />
                              {!event.passBuilderEnabled && <WalletPassUpgradeAlert />}
                              <WalletPassCustomization
                                event={event}
                                builderPath={eventPortalWalletPassBuilderPath(event.id)}
                              />
                            </Stack>
                          )}
                        </Confirmations>
                      </Route>
                    )}
                    {variant === "portal" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.AutomatedEmails)}>
                        <EmailResponders updateEvent={updateEvent} accountUsers={accountUsers} />
                      </Route>
                    )}
                    {variant === "forms" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.PortalSetup)}>
                        <PortalSetup event={event} updateEvent={updateEvent} />
                      </Route>
                    )}
                    {variant === "forms" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.Confirmations)}>
                        <Confirmations updateEvent={updateEvent} />
                      </Route>
                    )}
                    {variant === "forms" && (
                      <Route path={pathFor(StyleAndConfigurationSubpath.EmailResponders)}>
                        <EmailResponders updateEvent={updateEvent} accountUsers={accountUsers} />
                      </Route>
                    )}
                  </Switch>
                </Box>
              </Box>
            </Flex>
            <ScrollToTop containerRef={ref} />
          </Route>
        </Switch>
      </Flex>
      <StyleAndConfigurationModals
        modalName={selectedModal}
        onHide={handleCloseModal}
        event={eventWorkingCopy}
        onChangeEventWorkingCopy={onChangeEventWorkingCopy}
        updateEvent={updateEvent}
        onResetEventWorkingCopy={onResetEventWorkingCopy}
      />
    </Flex>
  )
}

export default StyleAndConfiguration
